import { useState, useEffect } from 'react'
import { Loader2, RefreshCw } from 'lucide-react'
import { useAuth } from '@/context/AuthContext'
import { toast } from '@/hooks/use-toast'
import { invokeEdgeFunction } from '@/lib/edge-functions'
import { supabase } from '@/integrations/supabase/client'
import { getInformalName } from '@/lib/teams'

// Mirrors getPreviousApiSeasonYear in supabase/functions/_shared/api-football.ts
function getPreviousApiSeason(leagueSeason: string): string {
  return String(parseInt(leagueSeason, 10) - 1)
}

export default function Admin() {
  const { isSuperAdmin } = useAuth()
  const [isSyncingTdd, setIsSyncingTdd] = useState(false)
  const [tddTeams, setTddTeams] = useState<{ competition_id: number; team_name: string; season: string }[]>([])

  const buildPrevSeasonMap = async () => {
    const { data: configs } = await supabase
      .from('league_configs')
      .select('api_football_league_id, season_format, league_id')

    const { data: leagues } = await supabase
      .from('leagues')
      .select('id, season')

    const prevSeasons = new Map<number, string>()
    for (const config of configs ?? []) {
      if (prevSeasons.has(config.api_football_league_id)) continue
      const league = leagues?.find(l => l.id === config.league_id)
      if (league?.season) {
        prevSeasons.set(
          config.api_football_league_id,
          getPreviousApiSeason(league.season),
        )
      }
    }
    return prevSeasons
  }

  const loadTddTeams = async () => {
    const { data, error } = await supabase
      .from('tdd_ineligible_teams')
      .select('competition_id, team_name, season')
      .is('league_id', null)
      .order('competition_id')
      .order('team_name')
    if (error) {
      console.error('Failed to load TDD teams:', error)
      return
    }
    const typed = data as { competition_id: number; team_name: string; season: string }[]
    const prevSeasons = await buildPrevSeasonMap()
    if (prevSeasons.size === 0) {
      setTddTeams(typed)
    } else {
      setTddTeams(typed.filter(t => t.season === prevSeasons.get(t.competition_id)))
    }
  }

  const handleSyncTddTeams = async (syncCompetitionId?: number) => {
    setIsSyncingTdd(true)
    try {
      const prevSeasons = await buildPrevSeasonMap()

      // For single-competition re-sync, derive season from TDD data if not in league configs
      if (syncCompetitionId && !prevSeasons.has(syncCompetitionId)) {
        const existing = tddTeams.find(t => t.competition_id === syncCompetitionId)
        if (existing?.season) {
          prevSeasons.set(syncCompetitionId, existing.season)
        }
      }

      const competitionIds = syncCompetitionId
        ? [syncCompetitionId]
        : [...prevSeasons.keys()]

      let totalSynced = 0
      for (const compId of competitionIds) {
        const prevSeason = prevSeasons.get(compId)
        if (!prevSeason) continue
        const { data, error } = await invokeEdgeFunction('fetch-standings', {
          body: { competitionId: compId, season: prevSeason },
        })
        if (error || data?.error) {
          toast({ title: `Failed: competition ${compId}`, description: error?.message || data?.error, variant: 'destructive' })
        } else {
          totalSynced += data?.tddIneligibleTeams?.length ?? 0
        }
      }

      toast({ title: 'TDD teams synced', description: `${totalSynced} ineligible teams across ${competitionIds.length} competition(s)` })
      await loadTddTeams()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unexpected error'
      toast({ title: 'Sync error', description: message, variant: 'destructive' })
    } finally {
      setIsSyncingTdd(false)
    }
  }

  useEffect(() => { if (isSuperAdmin) loadTddTeams() }, [isSuperAdmin])

  return (
    <div className="p-6">
      <div className="max-w-lg mx-auto space-y-8">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Global Tools</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Platform-wide tools and configuration
          </p>
        </div>

        <section className="space-y-3">
          <h2 className="text-sm font-medium text-zinc-500 dark:text-zinc-400 uppercase tracking-wider">
            TDD Ineligible Teams
          </h2>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            Top 6 teams from previous season that cannot be used for Top Dog Double. Synced from API-Football standings.
          </p>

          <div className="flex gap-2">
            <button
              onClick={() => handleSyncTddTeams()}
              disabled={isSyncingTdd}
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white
                         hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSyncingTdd ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync All Competitions
            </button>
          </div>

          {tddTeams.length > 0 && (
            <div className="space-y-3">
              {Object.entries(
                tddTeams.reduce((acc, t) => {
                  const key = `${t.competition_id}`
                  if (!acc[key]) acc[key] = []
                  acc[key].push(t)
                  return acc
                }, {} as Record<string, typeof tddTeams>)
              ).map(([compId, teams]) => {
                const compNames: Record<string, string> = { '39': 'EPL', '135': 'Serie A', '140': 'La Liga', '61': 'Ligue 1', '78': 'Bundesliga', '253': 'MLS' }
                const compName = compNames[compId] || `Competition ${compId}`
                return (
                  <div key={compId} className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                        {compName} <span className="text-zinc-500">(season {teams[0]?.season})</span>
                      </span>
                      <button
                        onClick={() => handleSyncTddTeams(Number(compId))}
                        disabled={isSyncingTdd}
                        className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline disabled:opacity-50"
                      >
                        Re-sync
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {teams.map(t => (
                        <span
                          key={t.team_name}
                          className="px-2 py-0.5 text-xs rounded-full bg-red-100 dark:bg-red-900/30
                                     text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
                        >
                          {getInformalName(t.team_name)}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {tddTeams.length === 0 && (
            <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
              No TDD ineligible teams loaded. Click "Sync All Competitions" to populate from API-Football.
            </p>
          )}
        </section>
      </div>
    </div>
  )
}
